let pyodide;
let capturedOutput = "";
let isLoading = false;
let loadingCallbacks = [];

function stdoutCallback(text) {
  capturedOutput += text + "\n";
}

// Check if Pyodide is already loaded
export function isPyodideLoaded() {
  return !!pyodide;
}

// Check if Pyodide is currently loading
export function isPyodideLoading() {
  return isLoading;
}

export async function loadPyodideOnce(onProgress) {
  if (pyodide) {
    return pyodide;
  }

  if (isLoading) {
    // Wait for existing load operation
    return new Promise((resolve) => {
      loadingCallbacks.push(resolve);
    });
  }

  isLoading = true;

  try {
    // Notify progress: Waiting for Pyodide script
    if (onProgress) onProgress('Initializing Python environment...');

    while (!window.loadPyodide) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // Notify progress: Loading Pyodide
    if (onProgress) onProgress('Downloading Python interpreter (this may take 10-30 seconds)...');

    pyodide = await window.loadPyodide({
      // Self-hosted Pyodide (public/pyodide) so the app runs Python 100% offline.
      indexURL: (process.env.PUBLIC_URL || "") + "/pyodide/",
      stdout: stdoutCallback,
      stderr: stdoutCallback,
    });

    // Default: raise error on input() when no test input provided
    pyodide.setStdin({ error: true });

    if (onProgress) onProgress('Python ready!');

    // Resolve any waiting promises
    loadingCallbacks.forEach(callback => callback(pyodide));
    loadingCallbacks = [];

    return pyodide;
  } finally {
    isLoading = false;
  }
}

// New: Provide test inputs for automated testing
export async function runPythonCode(code, testInputs = [], onProgress, setupAPI = null) {
  const py = await loadPyodideOnce(onProgress);  // Ensure loaded first
  capturedOutput = "";                 // Reset output

  // Setup custom API if provided (e.g., mock AI API for Golden AI levels)
  if (setupAPI && typeof setupAPI === 'function') {
    setupAPI(py);
  }

  // Configure stdin based on test inputs
  if (testInputs.length > 0) {
    let inputIndex = 0;
    const stdinProvider = () => {
      if (inputIndex < testInputs.length) {
        return testInputs[inputIndex++] + "\n";  // input() expects newline
      }
      return null;  // EOF after all inputs consumed
    };
    py.setStdin({ stdin: stdinProvider });
  } else {
    py.setStdin({ error: true });  // Safety: error if unexpected input()
  }

  let result = "";
  try {
    const pyResult = await py.runPythonAsync(code);
    if (pyResult !== undefined) {
      result = pyResult.toString();
    }
  } catch (err) {
    capturedOutput += (err.message || String(err));
  } finally {
    // Optional: reset to error mode after run (prevents leakage)
    py.setStdin({ error: true });
  }

  const fullOutput = capturedOutput.trim();
  return fullOutput + (result && fullOutput ? "\n" : "") + result;
}